import {
  Box,
  TextField as BaseTextField,
  styled,
  Typography,
} from '@mui/material';
import type {SearchInputProps, OptionType} from '..';
import {BaseSyntheticEvent, useEffect, useRef, useState} from 'react';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';

const TextField = styled(BaseTextField)(({theme}) => ({
  'textarea::placeholder': {
    fontStyle: 'normal',
  },
  '.MuiInputBase-root': {
    fontSize: '14px',
    lineHeight: '16px',
    fontWeight: 500,
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(0.6),
    paddingBottom: theme.spacing(0.6),
    borderRadius: theme.breakpoints.down('md')
      ? theme.spacing(3)
      : theme.spacing(4),
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
}));

function cleanName(value: string) {
  return value.replace(/\s+/g, ' ').trim();
}
const containsText = (text: string, searchText: string) =>
  text?.toLowerCase().indexOf(searchText?.toLowerCase()) > -1;

const SearchInput = (props: SearchInputProps) => {
  const {
    options,
    placeholder,
    onChange,
    value,
    name,
    error,
    variant,
    onSelect,
    maxCharLength,
    onBlur,
    onFocus,
    setDialogOpen,
    formikName,
    clusterValue,
    clusterOptions,
    serverSearch,
    onOptionSelect,
  } = props;
  const [searchText, setSearchText] = useState<OptionType>({
    label: '',
    value: '',
  });
  const [searchOptions, setSearchOptions] = useState<OptionType[]>(options);
  const [searchInputFocused, setSearchInputFocused] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [optionsMapping, setOptionsMapping] = useState<{[key: string]: string}>(
    {}
  );
  const optionsBoxRef = useRef(null);
  const inputBoxRef = useRef(null);
  const isLarge = useIsLargeDevice();
  useOutsideAlerter(optionsBoxRef);

  function useOutsideAlerter(ref) {
    useEffect(() => {
      function handleClickOutside(event: BaseSyntheticEvent) {
        if (
          ref.current &&
          !ref.current.contains(event.target) &&
          !(document.activeElement === inputBoxRef.current) &&
          !searchInputFocused
        ) {
          setSearchInputFocused(false);
        }
      }
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [ref]);
  }
  useEffect(() => {
    if (options?.length) {
      const om: {[key: string]: string} = {};
      options.map(option => {
        om[option.value] = option.label;
      });
      setOptionsMapping(om);
    }
  }, [options]);

  // useEffect(() => {
  //   const currentSearchOptions = options.filter(
  //     option =>
  //       option.label === searchText.label ||
  //       containsText(option.label.toString(), searchText.label)
  //   );
  //   setSearchOptions(currentSearchOptions);
  // }, [searchText]);
  useEffect(() => {
    let currentSearchOptions: OptionType[] = [];
    if (serverSearch) {
      currentSearchOptions = options;
    } else if (searchText.label) {
      currentSearchOptions = options.filter(
        option =>
          option.label === searchText.label ||
          containsText(option.label.toString(), searchText.label)
      );
    } else {
      if (clusterValue && clusterOptions?.length) {
        const clustered = clusterOptions.filter(
          (opt: any) => opt.cluster === clusterValue
        );
        currentSearchOptions = [...clustered];
      } else {
        currentSearchOptions = options;
      }
    }

    setSearchOptions(currentSearchOptions);
    setHighlightedIndex(-1);
  }, [searchText, options, clusterValue, clusterOptions, serverSearch]);

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      justifyContent={'flex-start'}
      alignItems={'flex-start'}
      gap={0}
      width={'100%'}
      position={'relative'}
    >
      <TextField
        ref={inputBoxRef}
        name={name}
        placeholder={placeholder}
        onFocus={(e: BaseSyntheticEvent) => {
          onFocus && onFocus(e);
          setSearchInputFocused(true);
        }}
        onClick={() => {
          if (!isLarge) {
            // Optional prop: a consumer with no mobile dialog to open still has to be
            // able to type in the field rather than take a TypeError on tap.
            if (formikName) {
              setDialogOpen?.(formikName);
            } else setDialogOpen?.(name);
          }
        }}
        value={value && optionsMapping[value] ? optionsMapping[value] : value}
        onChange={(e: BaseSyntheticEvent) => {
          setSearchInputFocused(true);
          if (maxCharLength) {
            e.target.value = e.target.value?.substring(0, maxCharLength);
          }
          setSearchText({label: cleanName(e.target?.value), value: ''});
          onChange && onChange(e);
        }}
        onBlur={(e: BaseSyntheticEvent) => {
          e.target.value = cleanName(e.target?.value);
          onBlur && onBlur(e);
        }}
        onKeyDown={e => {
          const dropdownVisible =
            searchInputFocused && isLarge && searchOptions.length > 0;
          if (e.key === 'Escape') {
            setSearchInputFocused(false);
          } else if (e.key === 'Tab') {
            setSearchInputFocused(false);
          } else if (e.key === 'ArrowDown') {
            if (dropdownVisible) {
              e.preventDefault();
              setHighlightedIndex(prev => (prev + 1) % searchOptions.length);
            }
          } else if (e.key === 'ArrowUp') {
            if (dropdownVisible) {
              e.preventDefault();
              setHighlightedIndex(
                prev => (prev - 1 + searchOptions.length) % searchOptions.length
              );
            }
          } else if (e.key === 'Enter') {
            if (dropdownVisible && highlightedIndex >= 0) {
              e.preventDefault();
              const selected = searchOptions[highlightedIndex];
              const event = {target: {name, value: selected.value}};
              onChange && onChange(event);
              if (variant === VersionVariants.VERSION_TWO && onSelect) {
                onSelect(event);
              }
              onOptionSelect &&
                onOptionSelect({
                  index: highlightedIndex,
                  value: selected.value,
                  label: selected.label,
                  optionsAbove: searchOptions.slice(0, highlightedIndex),
                });
              setSearchInputFocused(false);
              return;
            }
            if (variant === VersionVariants.VERSION_TWO && onSelect) {
              onSelect(e);
            }
            setSearchInputFocused(false);
          }
        }}
        variant="standard"
        InputProps={{
          disableUnderline: true,
          readOnly: !isLarge,
        }}
        fullWidth
        error={error}
        sx={{
          '.MuiInputBase-root': {
            border:
              variant === VersionVariants.VERSION_TWO
                ? 0
                : '1px solid  #7E7E7E33',
            borderColor: error
              ? 'red'
              : searchInputFocused
                ? '#33333390'
                : '#e0e0e0',
            ':hover': {
              borderColor: '#33333390',
            },
          },
        }}
      />
      <Box
        ref={optionsBoxRef}
        position={'absolute'}
        top={50}
        height={'fit-content'}
        maxHeight={200}
        width={'100%'}
        display={
          searchInputFocused && isLarge && searchOptions.length
            ? 'flex'
            : 'none'
        }
        flexDirection={'column'}
        justifyContent={'flex-start'}
        alignItems={'flex-start'}
        zIndex={100}
        border={'1px solid'}
        borderColor={'rgba(0, 0, 0, 0.12)'}
        borderRadius={2}
        sx={{
          background: '#fff',
          overflowY: searchOptions.length > 6 ? 'scroll' : 'hidden',
          // scrollbarWidth: 'none',
          // '-ms-overflow-style': 'none',
          // '&::-webkit-scrollbar': {
          //   display: 'none',
          // },
        }}
      >
        {searchOptions.slice(0, 50).map((so, idx) => {
          const isHighlighted = idx === highlightedIndex;
          return (
            <Box
              key={so.value}
              ref={
                isHighlighted
                  ? (el: HTMLElement | null) =>
                      el?.scrollIntoView({block: 'nearest'})
                  : undefined
              }
              display={'flex'}
              alignItems={'center'}
              justifyContent={'flex-start'}
              width={'100%'}
              py={1}
              px={2}
              sx={{
                cursor: 'pointer',
                background: isHighlighted
                  ? props.hiristColor
                    ? 'rgba(233, 99, 12, 0.08)'
                    : '#91B87929'
                  : undefined,
                ':hover': {
                  background: props.hiristColor
                    ? 'rgba(233, 99, 12, 0.08)'
                    : '#91B87929',
                },
              }}
              onMouseDown={e => e.preventDefault()}
              onMouseEnter={() => setHighlightedIndex(idx)}
              onClick={() => {
                let event = {
                  target: {
                    name: name,
                    value: so.value,
                  },
                };
                onChange && onChange(event);
                if (variant === VersionVariants.VERSION_TWO && onSelect) {
                  onSelect(event);
                }
                onOptionSelect &&
                  onOptionSelect({
                    index: idx,
                    value: so.value,
                    label: so.label,
                    optionsAbove: searchOptions.slice(0, idx),
                  });
                setSearchInputFocused(false);
              }}
            >
              <Typography
                variant="body3"
                width={'100%'}
                overflow={'hidden'}
                whiteSpace={'nowrap'}
                textOverflow={'ellipsis'}
                fontWeight={searchText.label == so.label ? 500 : 400}
              >
                {so.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SearchInput;
